import lodash from 'lodash'
import * as React from 'react'
import {compose, wrapDisplayName, onlyUpdateForKeys} from 'recompose'

import {autobind} from '@base/decorators'
import later from '@base/later'
import {cloneOrCreate} from '@base/react-utils/cloneOrCreate'

type WithCachedValueProps = {
  onChange?: Function | null
  value: string | undefined | null
}

type State = {
  cachedValue: string | undefined | null
  cachedSelectionStart: number
  cachedSelectionEnd: number
}

const withCachedValue =
  ({displayName, guard}: any = {}) =>
  (Component: any) => {
    // eslint-disable-next-line react/no-unsafe
    class WithCachedValue extends React.Component<WithCachedValueProps, State> {
      static displayName =
        displayName || wrapDisplayName(Component, 'withCachedValue')

      state = {
        cachedValue: undefined,
        cachedSelectionStart: 0,
        cachedSelectionEnd: 0,
      }

      doHandleFocusLater = later.delay(this.doHandleFocus, 32)
      doSetCursorLater = later.delay(this.doSetCursor, 32)
      inputRef?: HTMLInputElement = undefined

      constructor(props) {
        super(props)
        const {value} = props
        this.state = {
          cachedValue: value,
          cachedSelectionStart: value.length - 1,
          cachedSelectionEnd: value.length - 1,
        }

        if (props.focused) {
          this.doHandleFocusLater()
          this.doSetCursorLater()
        }
      }

      @autobind
      doSetCursor() {
        if (this.inputRef) {
          const {cachedSelectionStart, cachedSelectionEnd} = this.state
          this.inputRef.selectionStart = cachedSelectionStart
          this.inputRef.selectionEnd = cachedSelectionEnd
        }
      }

      @autobind
      doHandleFocus() {
        if (this.inputRef) {
          this.inputRef.focus()
        }
      }

      UNSAFE_componentWillReceiveProps(nextProps) {
        const allowed = guard(nextProps, this.state.cachedValue)
        if (allowed) {
          this.setState({cachedValue: nextProps.value}, () => {
            this.doSetCursorLater()
          })
        }
      }

      @autobind
      handleChange(event, ...rest) {
        const {value, selectionStart, selectionEnd} = event.target
        this.setState({
          cachedValue: value,
          cachedSelectionStart: selectionStart,
          cachedSelectionEnd: selectionEnd,
        })
        const {onChange} = this.props
        if (onChange) {
          onChange(event, ...rest)
        }
      }

      render() {
        return cloneOrCreate(Component, {
          ...lodash.omit(this.props, ['focused']),
          onChange: this.handleChange,
          value: this.state.cachedValue,
          ref: (r: HTMLInputElement) => (this.inputRef = r),
        })
      }
    }

    return WithCachedValue
  }

const isEmpty = (value: any) => {
  const empty = value === undefined || value === '' || value === null

  return empty
}

const SemiControlledInput = compose(
  onlyUpdateForKeys(['value']),
  withCachedValue({
    guard: (props, cached) =>
      !props.focused ||
      cached === undefined ||
      isEmpty(props.value) !== isEmpty(cached),
    displayName: 'Focus Controlled Input',
  })
  // @ts-expect-error we're passing an HTML tag here, which is not compatible with compose signature, but it works...
)('input')

export default SemiControlledInput
